import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import AuthDialogsWhenAsked from "./components/AuthDialogs/AuthDialogsWhenAsked";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import lazyWithRetry from "./utils/lazyWithRetry";
import { ReferralProvider } from "./referral/ReferralContext";
import { Helmet } from "react-helmet";
import { publicRoutes, publicPaths, languageForLocation } from "./rendering/publicRoutes";

// Lazy-load every page, and the site's header, footer and trial banner, so
// the shell holds only what every page needs: the homepage draws its own
// chrome, and an anonymous visitor never sees the banner. lazyWithRetry
// recovers from stale-chunk fetches after a deploy.
const Header = lazyWithRetry(() => import("./components/Header/Header"));
const TrialBanner = lazyWithRetry(() => import("./components/Header/TrialBanner"));
const Footer = lazyWithRetry(() => import("./components/Footer/Footer"));
const Unsubscribe = lazyWithRetry(() => import("./Pages/Newsletter/Unsubscribe"));
const VerifyAccount = lazyWithRetry(() => import("./Pages/Auth/VerifyAccount"));
const ResetPassword = lazyWithRetry(() => import("./Pages/Auth/ResetPassword"));
const ConfirmEmailChange = lazyWithRetry(() => import("./Pages/Auth/ConfirmEmailChange"));
const Payment = lazyWithRetry(() => import("./components/Payment"));
const PaymentSuccess = lazyWithRetry(() => import("./components/PaymentSuccess"));
const PaymentFailed = lazyWithRetry(() => import("./components/PaymentFailed"));
const AccountSetting = lazyWithRetry(() => import("./Pages/AccountSetting"));
const NotFound = lazyWithRetry(() => import("./Pages/Error/NotFound"));
const Admin = lazyWithRetry(() => import("./Pages/Admin"));
const SignupPage = lazyWithRetry(() => import("./Pages/Signup/SignupPage"));
const TourPage = lazyWithRetry(() => import("./Pages/Tour/TourPage"));
const Welcome = lazyWithRetry(() => import("./Pages/Welcome/Welcome"));
const InstallPage = lazyWithRetry(() => import("./Pages/Install/InstallPage"));

// Tiny redirect for the IT-locale alias /stampa/:slug → /press/:slug.
// React Router's <Navigate> doesn't interpolate route params, so we
// resolve them here and forward.
const StampaSlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/press/${slug}`} replace />;
};

const RouteFallback = () => (
  <div
    style={{
      minHeight: "70vh",
      background: "#0D0D0D",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CircularProgress size="large" />
  </div>
);

// Full-bleed onboarding funnel pages carry their own minimal chrome; the
// site Header/Footer would break the split layout and add exit ramps
// mid-funnel. Exact-match list, mirroring AUTH_FLOW_ROUTES' matching style.
const CHROME_HIDDEN_ROUTES = ["/signup", "/tour"];
// The homepage, the manifesto and the plans draw their own nav and footer.
const OWN_CHROME_ROUTES = ["/", "/en", "/manifesto", "/en/manifesto", "/plans", "/en/plans"];

const usePathLanguage = () => {
  const { pathname, search } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const nextLang = languageForLocation(pathname, search);
    if ((i18n.language || "it").slice(0, 2) !== nextLang) {
      i18n.changeLanguage(nextLang);
    }
  }, [i18n, pathname, search]);
};

const AppContent = () => {
  const { pathname } = useLocation();
  usePathLanguage();
  const hideChrome = CHROME_HIDDEN_ROUTES.includes(pathname);
  const ownChrome = OWN_CHROME_ROUTES.includes(pathname);
  const { i18n } = useTranslation();
  return (
    <>
      <Helmet htmlAttributes={{ lang: (i18n.language || "it").slice(0, 2) }}>
        <title>A.V.R.A.H.</title>
        <meta name="robots" content={publicPaths.includes(pathname) ? "index, follow" : "noindex, follow"} />
        <meta property="og:site_name" content="A.V.R.A.H." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://avrah.it/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://avrah.it/og-image.png" />
      </Helmet>
      <ScrollToTop />
      {!hideChrome && !ownChrome && <Suspense fallback={null}><Header /></Suspense>}
      {/* Whatever header a page draws, or none, it can open these. */}
      <AuthDialogsWhenAsked />
      {/* Global post-login status/CTA strip. Self-hides unless the user is
          logged in with a flagged or not-entitled subscription state, so a
          non-entitled user sees their checkout prompt right after login on
          whatever page they land on. Hidden on the funnel routes that drop
          the Header. */}
      {!hideChrome && <Suspense fallback={null}><TrialBanner /></Suspense>}
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Keyed by path, so a page and its English twin are two
              pages: switching language starts it afresh in the other. */}
          {publicRoutes.map(({ path, Component }) => (
            <Route key={path} path={path} element={<Component key={path} />} />
          ))}
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/Plans" element={<Navigate to="/plans" replace />} />
          {/* IT-friendly aliases that funnel to the canonical /press URLs. */}
          <Route path="/stampa" element={<Navigate to="/press" replace />} />
          <Route path="/stampa/:slug" element={<StampaSlugRedirect />} />
          <Route path="/newsletter/unsubscribe" element={<Unsubscribe />} />
          <Route path="/verify-account" element={<VerifyAccount />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
          <Route path="/signup" element={<SignupPage />} />
          {/* Signup funnel: post-signup email verification. Public on
              purpose - the verify phase happens before login. */}
          <Route path="/tour" element={<TourPage />} />
          <Route path="/install/:platform" element={<InstallPage />} />
          {/* Public on purpose: a buyer can come back from Stripe logged out,
              and this page logs them in from the checkout's session_id. */}
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/admin" element={<Admin />} />
            <Route
              path="/subscribe-plan/:planId"
              element={
                <Payment />
              }
            />
            <Route path="/account-settings" element={<AccountSetting />} />
            <Route path="/payment-failed" element={<PaymentFailed />} />
            <Route path="/welcome" element={<Welcome />} />
          </Route>
          {/* Catch-all: any unmatched path renders the 404 stage. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {!hideChrome && !ownChrome && <Suspense fallback={null}><Footer /></Suspense>}
    </>
  );
};

export const AppContentWithProviders = () => (
  <ErrorBoundary>
    <ReferralProvider><AppContent /></ReferralProvider>
  </ErrorBoundary>
);

const App = () => {
  return (
    <Router>
      <AppContentWithProviders />
    </Router>
  );
};

export default App;
